import { inject, Injectable, signal } from '@angular/core';
import { ECapabilities, EDocumentType, IAccount, ICompanyDocument } from '@okcargo-apps/shared-domain-ionic';
import {
  CompanyQueryService,
  DateService,
  DocumentUploaderCommandService,
} from '@okcargo-apps/shared-infrastructure-ionic';
import { UploadCompanyDocumentCommand } from '@okcargo/command-processor';
import {
  DocumentMetadataFieldNames,
  DocumentMetadataResponse,
  DocumentType,
  FilterCombinator,
  FilterComparator,
  OrderType,
  SearchCarrierDocumentMetadataQuery,
} from '@okcargo/query-processor';
import { addDays } from 'date-fns';
import { BehaviorSubject, from, map, Observable, tap } from 'rxjs';
import { AccountService } from '../account/account.service';
import { DownloadDocumentService } from '../download-document/download-document.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private readonly companyQueryService = inject(CompanyQueryService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly accountService = inject(AccountService);
  private readonly documentUploaderCommandService = inject(DocumentUploaderCommandService);
  private readonly dateService = inject(DateService);

  page = 0;
  size = 200;

  private readonly _companyDocuments$ = new BehaviorSubject<ICompanyDocument[]>([]);
  private readonly _expiredOrNotUploadedDocuments$ = new BehaviorSubject<ICompanyDocument[]>([]);

  public companyDocumentsLoading = signal(true);

  private account!: IAccount;

  constructor() {
    this.accountService.account$.subscribe((account) => {
      if (account) {
        this.account = account;
        this.getCompanyDocuments();
      }
    });
  }

  get companyDocuments$(): Observable<ICompanyDocument[]> {
    return this._companyDocuments$.asObservable();
  }

  get expiredOrNotUploadedDocuments$(): Observable<ICompanyDocument[]> {
    return this._expiredOrNotUploadedDocuments$.asObservable();
  }

  public refreshCompanyDocuments(): void {
    this.getCompanyDocuments();
  }

  private getCompanyDocuments(): void {
    if (this.account.capabilities.includes(ECapabilities.ADMINISTRATIVE_DOCUMENTS)) {
      const filters = [];
      filters.push({
        combinator: FilterCombinator.NONE,
        field: DocumentMetadataFieldNames.COMPANY_ID,
        comparator: FilterComparator.EQ,
        value: this.account.company.id,
        group: null,
      });
      if (!this.account.company.isInternational) {
        filters.push({
          combinator: FilterCombinator.AND,
          field: DocumentMetadataFieldNames.TYPE,
          comparator: FilterComparator.IN,
          value:
            DocumentType.BANK_OWNERSHIP_CERTIFICATE +
            '|' +
            DocumentType.SOCIAL_SECURITY_PAYMENT_CERTIFICATE +
            '|' +
            DocumentType.TAX_AUTHORITIES_PAYMENT_CERTIFICATE,
        });
      } else {
        filters.push({
          combinator: FilterCombinator.AND,
          field: DocumentMetadataFieldNames.TYPE,
          comparator: FilterComparator.IN,
          value:
            DocumentType.BANK_OWNERSHIP_CERTIFICATE +
            '|' +
            DocumentType.ACTIVITY_LICENSE +
            '|' +
            DocumentType.EUROPEAN_LICENSE +
            '|' +
            DocumentType.GOODS_INSURANCE +
            '|' +
            DocumentType.RLC,
        });
      }

      const query = {
        criteriaPayload: {
          filters: filters,
          order: [
            {
              field: DocumentMetadataFieldNames.NAME,
              type: OrderType.ASC,
            },
          ],
          pageIndex: this.page,
          pageSize: this.size,
        },
      } as unknown as SearchCarrierDocumentMetadataQuery;

      this.companyDocumentsLoading.set(true);

      from(this.companyQueryService.searchCarrierDocumentMetadata(query))
        .pipe(
          /*
          1.- Transform dates from string to Date.
          2.- Only the last uploaded documents of each type.
          3.- Add download links (use downloadDocumentService).
              In this step, the ICompanyDocument interface is transformed, no mapping is necessary.
          4.- Add required documents that are not uploaded without data (only type).
              That's why we need to change interfaces before we get here.
          5.- Send values to observables.
           */
          map((documents: DocumentMetadataResponse[]) => {
            return documents.map((doc) => ({
              ...doc,
              createdAt: new Date(doc.createdAt),
              expiresOn: new Date(doc.expiresOn),
            }));
          }),

          map((documents: DocumentMetadataResponse[]) => {
            const groupedDocuments = documents.reduce(
              (acc: { [key: string]: DocumentMetadataResponse }, doc: DocumentMetadataResponse) => {
                if (!acc[doc.type] || doc.createdAt > acc[doc.type].createdAt) {
                  acc[doc.type] = doc;
                }
                return acc;
              },
              {},
            );
            return Object.values(groupedDocuments).map((doc) => {
              return {
                ...doc,
                url: this.downloadDocumentService.getDownloadDocumentURL(doc),
              } as unknown as ICompanyDocument;
            });
          }),

          map((documents: ICompanyDocument[]) => {
            const requiredDocumentTypes = !this.account.company.isInternational
              ? [
                  EDocumentType.BANK_OWNERSHIP_CERTIFICATE,
                  EDocumentType.SOCIAL_SECURITY_PAYMENT_CERTIFICATE,
                  EDocumentType.TAX_AUTHORITIES_PAYMENT_CERTIFICATE,
                ]
              : [
                  EDocumentType.BANK_OWNERSHIP_CERTIFICATE,
                  EDocumentType.ACTIVITY_LICENSE,
                  EDocumentType.EUROPEAN_LICENSE,
                  EDocumentType.GOODS_ASSURANCE,
                  EDocumentType.CIVIL_RESPONSABILITY_ASSURANCE,
                ];

            requiredDocumentTypes.forEach((requiredType) => {
              const documentExists = documents.some((doc) => doc.type === requiredType);
              if (!documentExists) {
                documents.push({
                  id: '',
                  type: requiredType,
                } as ICompanyDocument);
              }
            });
            return documents;
          }),

          tap((documents) => this._companyDocuments$.next(documents)),
          tap(() => this.filterExpiredOrNotUploadedDocuments()),
          tap(() => this.companyDocumentsLoading.set(false)),
        )
        .subscribe({
          error: (error) => {
            console.error('Error loading company documents', error);
            this.companyDocumentsLoading.set(false);
          },
        });
    }
  }

  public async uploadCompanyDocument(
    formData: any,
    fileInput: File | string,
    documentType: EDocumentType,
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const documentToBeUpdated = this._companyDocuments$.getValue().find((doc) => doc.type === documentType);

      if (documentToBeUpdated) {
        let base64Data: string;
        let fileName: string;
        let fileType: string;

        /*
        For now, all that is uploaded are files because if it's an image from the camera
        then the base64 is transformed into a PDF file in the JpgToPdfService service.

        The ELSE block is always executed.
         */
        if (typeof fileInput === 'string') {
          base64Data = fileInput;
          fileName = 'document.pdf'; // TODO replace filename
          fileType = 'application/pdf'; // TODO reflace filetype

          this.processCommandUploadOrReuploadDocument(
            documentToBeUpdated,
            base64Data,
            formData,
            fileType,
            documentType,
            fileName,
            resolve,
            reject,
          );
        } else if (fileInput instanceof File) {
          const reader = new FileReader();

          reader.onload = async (readerEvent: ProgressEvent<FileReader>) => {
            // TODO review this
            const fileData = (readerEvent.target as FileReader).result as string;
            const fileType = fileInput.type;
            const fileName = fileInput.name;
            // const extension = fileName.substring(fileName.lastIndexOf('.'));
            // const extension = '.pdf'; // TODO always pdf? or maybe convert to PDF
            const base64Data = fileData.substring(fileData.indexOf(',') + 1);
            // const uploadFileName = fileInput.name;

            await this.processCommandUploadOrReuploadDocument(
              documentToBeUpdated,
              base64Data,
              formData,
              fileType,
              documentType,
              fileName,
              resolve,
              reject,
            );
          };

          reader.onerror = (error) => {
            reject(error);
          };

          reader.readAsDataURL(fileInput);
        }
      } else {
        console.error('Wrong documentType', documentType);
        reject('Wrong documentType');
      }
    });
  }

  private async processCommandUploadOrReuploadDocument(
    documentToBeUpdated: any,
    base64Data: string,
    formData: any,
    fileType: string,
    documentType: EDocumentType,
    fileName: string,
    resolve: (value: string | PromiseLike<string>) => void,
    reject: (reason?: any) => void,
  ): Promise<void> {
    const command = {
      documentId: crypto.randomUUID(), // always new id
      data: base64Data,
      description:
        formData.iban !== '' ? formData.iban : formData.assuranceQuantity !== '' ? formData.assuranceQuantity : '',
      expiresOn:
        documentType !== EDocumentType.BANK_OWNERSHIP_CERTIFICATE
          ? new Date(formData.expirationDate)
          : addDays(new Date(), 365 * 10),
      companyId: this.account.company.id,
      contentType: fileType,
      name: documentType,
      type: documentType,
      deleteOn: null,
    };

    try {
      // if (documentToBeUpdated.id && documentToBeUpdated.id !== '') {
      //   await this.documentUploaderCommandService.reUploadCompanyDocument(
      //     command as unknown as ReUploadCompanyDocumentCommand,
      //   );
      // } else {
      await this.documentUploaderCommandService.uploadCompanyDocument(
        command as unknown as UploadCompanyDocumentCommand,
      );
      // }

      setTimeout(() => {
        this.refreshCompanyDocuments();
      }, 2000);

      resolve(fileName);
    } catch (error) {
      console.warn('File not saved ' + JSON.stringify(error));
      reject(error);
    }
  }

  private filterExpiredOrNotUploadedDocuments(): void {
    const currentDocuments = this._companyDocuments$.getValue();
    const expiredOrNotUploadedDocuments = currentDocuments.filter((doc) => {
      if (doc.expiresOn) return new Date(doc.expiresOn) < new Date();
      return true;
    });
    this._expiredOrNotUploadedDocuments$.next(expiredOrNotUploadedDocuments);
  }
}
